import { AudioManager } from '../audio/AudioManager';
import { canSelectLocation } from '../content/locations';
import { DialogueService } from '../domain/dialogue/DialogueService';
import { EffectExecutor, type SceneNavigator } from '../domain/effects/EffectExecutor';
import { InventoryService } from '../domain/inventory/InventoryService';
import { ConditionEvaluator } from '../domain/rules/ConditionEvaluator';
import { LocalStorageSaveRepository } from '../domain/save/SaveRepository';
import { GameStateStore, createInitialState } from '../domain/state/GameStateStore';
import { createGame, type GameController } from '../game/createGame';
import { EventBus } from '../game/events/EventBus';
import { createGameRuntime, installGameRuntime } from '../game/runtime';
import { AppShell } from '../ui/AppShell';

export function bootstrap(): void {
  const root = document.getElementById('game-ui');
  if (!root) throw new Error('DOM-elementet #game-ui mangler.');

  const events = new EventBus();
  const saves = new LocalStorageSaveRepository();
  const store = new GameStateStore(saves.load() ?? createInitialState());
  const evaluator = new ConditionEvaluator();
  const inventory = new InventoryService(store);
  const dialogue = new DialogueService(store, evaluator, events);
  const audio = new AudioManager(store.snapshot.settings);
  let controller: GameController | null = null;
  const navigator: SceneNavigator = {
    openScene(sceneId, locationId) {
      controller?.openScene(sceneId, locationId);
    },
    returnToMap() {
      controller?.returnToMap();
    },
    playAnimation(animationId) {
      controller?.playAnimation(animationId);
    },
  };
  const effects = new EffectExecutor(store, inventory, evaluator, dialogue, events, navigator);
  const runtime = createGameRuntime(events, store, evaluator);
  installGameRuntime(runtime);

  new AppShell(
    root,
    events,
    store,
    saves,
    inventory,
    evaluator,
    dialogue,
    effects,
    audio,
    runtime.debugHotspots,
  );
  controller = createGame(events);

  events.on('ui:start', () => {
    if (store.snapshot.currentSceneId) {
      controller?.openScene(
        store.snapshot.currentSceneId,
        store.snapshot.currentLocationId ?? 'soldaterboegen',
      );
    } else {
      controller?.returnToMap();
    }
  });
  events.on('ui:openLocation', ({ location }) => {
    if (!location.sceneId || !canSelectLocation(location, store.snapshot.completedPuzzles)) return;
    store.update((draft) => {
      draft.currentLocationId = location.id;
      draft.currentSceneId = location.sceneId ?? null;
      if (!draft.visitedLocations.includes(location.id)) draft.visitedLocations.push(location.id);
    });
    controller?.openScene(location.sceneId, location.id);
  });
  events.on('ui:returnToMap', () => {
    store.update((draft) => {
      draft.currentLocationId = null;
      draft.currentSceneId = null;
    });
    controller?.returnToMap();
  });
  events.on('audio:theme', ({ theme }) => audio.setTheme(theme));
  events.on('audio:sfx', ({ soundId }) => audio.playSfx(soundId));
  let uiModalBlocked = true;
  events.on('ui:inputBlocked', ({ blocked }) => {
    uiModalBlocked = blocked;
    if (controller) controller.game.input.enabled = !blocked;
  });
  const interactiveSelector =
    'button, input, select, .action-menu, .dialogue-panel, .modal-backdrop, .start-screen, .debug-panel';
  root.addEventListener('pointerover', (event) => {
    if ((event.target as Element).closest(interactiveSelector) && controller) {
      controller.game.input.enabled = false;
    }
  });
  root.addEventListener('pointerout', (event) => {
    const related = event.relatedTarget;
    if (related instanceof Element && related.closest(interactiveSelector)) return;
    if (controller) controller.game.input.enabled = !uiModalBlocked;
  });
}
